import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const ThemeContext = createContext(null);

/**
 * Theme provider.
 * - Persists the authenticated user's selected theme on the backend (`/auth/me/theme`).
 * - Applies `data-theme="light"` to <html> when active; absence ⇒ default dark.
 * - When the user is unauthenticated (e.g. /login), the data-theme attribute is cleared
 *   so the login page always renders the dark brand.
 */
export function ThemeProvider({ children }) {
  const { user, ready, refresh } = useAuth();
  const [theme, setThemeState] = useState("light");

  // sync from user record once auth is ready
  useEffect(() => {
    if (!ready) return;
    if (user && user.theme_preference) {
      setThemeState(user.theme_preference === "dark" ? "dark" : "light");
    } else {
      setThemeState("light");
    }
  }, [user, ready]);

  // apply to <html>. Root CSS is light by default; we add data-theme="dark"
  // to switch tokens. Login (unauthenticated) always renders light over the
  // mountain backdrop.
  useEffect(() => {
    const root = document.documentElement;
    if (!user) { root.removeAttribute("data-theme"); return; }
    if (theme === "dark") root.setAttribute("data-theme", "dark");
    else root.removeAttribute("data-theme");
  }, [theme, user]);

  const setTheme = useCallback(async (next) => {
    setThemeState(next);
    if (!user) return;
    try {
      await api.patch("/auth/me/theme", { theme: next });
      // keep AuthContext.user in sync so a hard refresh restores the right theme
      if (refresh) refresh();
    } catch {
      /* swallow — UI already updated; backend will re-sync on next login */
    }
  }, [user, refresh]);

  const toggle = useCallback(() => {
    setTheme(theme === "light" ? "dark" : "light");
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext) || { theme: "light", setTheme: () => {}, toggle: () => {} };
